import { useState, useEffect, useCallback } from 'react';
import {
  forecastService,
  extractForecastBlock,
  mapForecastPoints,
} from '../../services/forecastService';
import { useWeatherLocation } from '../../contexts/WeatherLocationContext';
import { toForecastApiString } from '../../services/locationManager';
import {
  DEFAULT_FORECAST_INTERVAL,
  getIntervalConfig,
} from './forecastIntervals';

export default function useForecastDashboard() {
  const {
    activeLocation,
    loading: locationLoading,
    error: locationError,
    isDefault,
    isLive,
    activateCurrentLocation,
  } = useWeatherLocation();

  const [points, setPoints] = useState([]);
  const [place, setPlace] = useState(null);
  const [timeZone, setTimeZone] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState(
    DEFAULT_FORECAST_INTERVAL
  );
  const [previewQuery, setPreviewQuery] = useState(null);

  const fetchForecast = useCallback(
    async (locationOrString, intervalId = selectedInterval) => {
      const config = getIntervalConfig(intervalId);
      if (!config) return false;

      try {
        setFetchLoading(true);
        setFetchError(null);

        const locationString =
          typeof locationOrString === 'string'
            ? locationOrString
            : toForecastApiString(locationOrString);

        const response = await forecastService.getForecast(locationString, {
          from: config.from,
          to: config.to,
          interval: config.interval,
          limit: config.limit,
        });

        const { periods, place: apiPlace, timeZone: tz } =
          extractForecastBlock(response);

        setPlace(apiPlace);
        setTimeZone(tz);
        setPoints(mapForecastPoints(periods, tz, intervalId));
        return true;
      } catch (err) {
        console.error('Forecast dashboard fetch:', err);
        setFetchError(err.message || 'Failed to fetch forecast data');
        setPoints([]);
        return false;
      } finally {
        setFetchLoading(false);
      }
    },
    [selectedInterval]
  );

  useEffect(() => {
    if (activeLocation && !previewQuery) {
      fetchForecast(activeLocation, selectedInterval);
    }
  }, [activeLocation, selectedInterval, previewQuery, fetchForecast]);

  useEffect(() => {
    if (previewQuery) {
      fetchForecast(`${previewQuery},`, selectedInterval);
    }
  }, [previewQuery, selectedInterval, fetchForecast]);

  const searchLocation = useCallback((term) => {
    if (term?.trim()) setPreviewQuery(term.trim());
  }, []);

  const clearPreview = useCallback(() => setPreviewQuery(null), []);

  const getCurrentLocation = useCallback(async () => {
    setPreviewQuery(null);
    return activateCurrentLocation();
  }, [activateCurrentLocation]);

  const location = previewQuery
    ? { name: previewQuery, city: previewQuery }
    : activeLocation;

  return {
    location,
    place,
    timeZone,
    points,
    loading: locationLoading || fetchLoading,
    error: fetchError || locationError,
    selectedInterval,
    setSelectedInterval,
    previewQuery,
    clearPreview,
    getCurrentLocation,
    searchLocation,
    isCurrentLocation: isLive,
    isDefaultLocation: isDefault,
    refetch: () =>
      fetchForecast(
        previewQuery ? `${previewQuery},` : activeLocation,
        selectedInterval
      ),
  };
}
